import React, {
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  DataGrid,
  GridColDef,
  GridFilterOperator,
  GridValueGetterParams,
  getGridStringOperators,
  getGridNumericOperators,
  getGridDateOperators,
  GridInitialState,
  useGridApiRef,
} from "@mui/x-data-grid";
import { Box, Button, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import dayjs from "dayjs";

import {
  LocalDB,
  // TODO: Implement offline
  // getDataFromLocalDB,
} from "../../utils/localDB";

import { useApiStore } from "../../store";
import useAuthorizedPages from "../../hooks/useAuthorizedPages";
import useI18n from "../../hooks/useI18n";

// TODO: Implement offline
// import useOnlineStatus from "../../hooks/useOnlineStatus";

export interface DataListProps {
  localDB: LocalDB;
  store: string;
  fields: IListColumn[];
  pageId: string;
  title: string;
  primaryKey?: string;
}

export interface IListColumn {
  id: string;
  label: string;
  width?: number;
  format?: string;
  type?: "string" | "number" | "date";
  lookupKey?: string; // Key to lookup labels from v_grouped_church_and_baptism_and_worship (e.g., "worship_id")
}

type ColumnType = "date" | "number" | "string";

const DATE_KEYWORDS = ["date", "dob", "birthday"];
const DEFAULT_INITIAL_STATE: GridInitialState = {
  pagination: {
    paginationModel: {
      pageSize: 10,
    },
  },
};

const DataList: React.FC<DataListProps> = ({
  localDB,
  store,
  fields,
  pageId,
  title,
  primaryKey = "id",
}) => {
  const { isCurrentPageAuthorized } = useAuthorizedPages();
  const navigate = useNavigate();
  const { t } = useI18n();

  // TODO: Implement offline
  // const isOnline = useOnlineStatus();

  const { getAllData, showModal, getOptionsLocally } = useApiStore(
    (state) => state,
  );
  const [rows, setRows] = useState<any[]>([]);

  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = useState<GridInitialState>();
  const dataGridLocalStorageKey = `${localDB.name}_data_grid_states`;
  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();

      // 1. ALWAYS read the freshest state right before saving
      // to prevent overwriting data from other tabs.
      const rawStorage = localStorage.getItem(dataGridLocalStorageKey);
      const allStates = rawStorage ? JSON.parse(rawStorage) : {};

      // 2. Merge the current page's state into the master object
      allStates[pageId] = currentState;

      // 3. Save it back
      localStorage.setItem(dataGridLocalStorageKey, JSON.stringify(allStates));
    }
  }, [apiRef, pageId, dataGridLocalStorageKey]);
  useLayoutEffect(() => {
    // 1. Pull the master object
    const rawStorage = localStorage?.getItem(dataGridLocalStorageKey);
    const allStates = rawStorage ? JSON.parse(rawStorage) : {};

    // 2. Extract just this page's state, or default to an empty object
    const pageState = allStates[pageId];

    setInitialState(pageState || {});

    // Handle refresh and navigating away/refreshing
    window.addEventListener("beforeunload", saveSnapshot);

    return () => {
      // In case of an SPA remove the event-listener
      window.removeEventListener("beforeunload", saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot, pageId, dataGridLocalStorageKey]);

  // Helper to lookup label for ID columns (e.g., worship_id -> "1 - Saturday - Bayview Cantonese")
  const getLookupLabel = (lookupKey: string, idValue: any): string => {
    if (idValue === null || idValue === undefined || idValue === "") {
      return "";
    }
    const options = getOptionsLocally(localDB, lookupKey);
    // options is array of { label, value } where value matches the ID
    const found = options.find(
      (opt: { label: string; value: string }) =>
        String(opt.value) === String(idValue),
    );
    if (found) {
      // Check if the label already starts with the ID to avoid duplication
      const idString = String(idValue);
      if (
        found.label.startsWith(idString + " - ") ||
        found.label.startsWith(idString + " ")
      ) {
        return found.label;
      }
      return `${idValue} - ${found.label}`;
    }
    return String(idValue); // Fallback to just showing the ID
  };

  const fetchData = async () => {
    let records: any[] = [];
    const requisitions = await getAllData(pageId);
    if (requisitions && requisitions.length > 0) {
      requisitions.forEach((item: any) => {
        records.push({ ...item, is_local: false });
      });

      setRows(records);
    }
  };

  useEffect(
    () => {
      if (!isCurrentPageAuthorized) {
        showModal(
          "Unauthorized Access",
          "You do not have access to this page.",
          undefined,
          undefined,
          "SignIn",
        );

        return;
      }

      fetchData();

      // TODO: Implement offline
      // let records: any[] = [];

      // // Get data
      // getDataFromLocalDB(localDB, store).then(async (value) => {
      //   // Get data from localstorage
      //   value?.forEach((item: any) => {
      //     records.push({ ...item, is_local: true });
      //   });

      //   // Get data from database
      //   if (isOnline) {
      //     const requisitions = await getAllData(pageId);
      //     if (requisitions && requisitions.length > 0) {
      //       requisitions.forEach((item: any) => {
      //         records.push({ ...item, is_local: false });
      //       });
      //     }
      //   }

      //   setRows(records);
      // });
    },
    // eslint-disable-next-line
    [isCurrentPageAuthorized],
  );

  const applyPlaceholder = (
    operators: GridFilterOperator[],
    placeholder: string,
  ): GridFilterOperator[] =>
    operators.map((operator) => ({
      ...operator,
      InputComponentProps: {
        ...operator.InputComponentProps,
        placeholder,
      },
    }));

  const looksLikeDateField = (field: IListColumn): boolean => {
    if (field.format) return true;

    const label = (field.label || "").toLowerCase();
    const id = (field.id || "").toLowerCase();
    return DATE_KEYWORDS.some(
      (keyword) => id.includes(keyword) || label.includes(keyword),
    );
  };

  const inferColumnType = (fieldId: string, field: IListColumn): ColumnType => {
    if (field.type) return field.type;

    if (looksLikeDateField(field)) {
      return "date";
    }

    for (const record of rows) {
      const rawValue = record[fieldId];
      if (rawValue === null || rawValue === undefined || rawValue === "") {
        continue;
      }

      if (rawValue instanceof Date) {
        return "date";
      }

      const value = typeof rawValue === "string" ? rawValue.trim() : rawValue;

      if (typeof value === "string") {
        const strictIso = dayjs(value, ["YYYY-MM-DD", "YYYY/MM/DD"], true);
        if (strictIso.isValid()) {
          return "date";
        }
      }

      const asNumber = Number(value);
      if (!Number.isNaN(asNumber)) {
        return "number";
      }

      if (dayjs(value).isValid()) {
        return "date";
      }

      return "string";
    }

    return "string";
  };

  const getExampleValue = (columnId: string): string | null => {
    for (const row of rows) {
      const rawValue = row[columnId];
      if (rawValue === null || rawValue === undefined) continue;
      if (typeof rawValue === "number" || typeof rawValue === "boolean") {
        return String(rawValue);
      }
      const value = rawValue?.toString().trim();
      if (!value) continue;

      const parsedDate = dayjs(value);
      if (parsedDate.isValid() && value.includes("-")) {
        return parsedDate.format("YYYY-MM-DD");
      }

      return value;
    }

    return null;
  };

  const columns: GridColDef[] = fields.map((field) => {
    const exampleValue = getExampleValue(field.id);
    const columnType = inferColumnType(field.id, field);
    const exampleText = exampleValue
      ? `e.g. ${exampleValue}`
      : columnType === "date"
        ? "e.g. 2025-01-01"
        : columnType === "number"
          ? "e.g. 100"
          : "e.g. Enter filter value";

    const filterOperators =
      columnType === "date"
        ? applyPlaceholder(getGridDateOperators(), exampleText)
        : columnType === "number"
          ? applyPlaceholder(getGridNumericOperators(), exampleText)
          : applyPlaceholder(getGridStringOperators(), exampleText);

    const fieldId = field.id;

    const headerText = t(field.label);
    // Estimate width based on header text length (approx 10px per char) + some padding
    // Use the larger of: configured width, or calculated header width
    const calculatedWidth = Math.max(
      field.width || 150,
      headerText.length * 15 + 20,
    );

    const column: GridColDef = {
      field: field.id,
      headerName: headerText,
      width: calculatedWidth,
      filterOperators,
      renderCell: (params) => {
        let displayValue =
          params.formattedValue != null ? params.formattedValue : params.value;
        if (Array.isArray(displayValue)) {
          displayValue = displayValue.join(", ");
        }
        return (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.5",
              padding: "8px 0",
            }}
          >
            {displayValue}
          </div>
        );
      },
    };

    if (columnType === "date") {
      column.type = "date";
      column.valueGetter = (params: GridValueGetterParams) => {
        const raw = params.row[fieldId];
        if (!raw) return null;
        const parsed = dayjs(raw);
        return parsed.isValid() ? parsed.toDate() : null;
      };
      column.valueFormatter = (params) => {
        if (!params.value) return "";
        const parsed = dayjs(params.value as Date);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
      };
    } else if (columnType === "number" && !field.lookupKey) {
      // Only apply number type if no lookupKey (lookup values should be strings)
      column.type = "number";
      column.valueGetter = (params: GridValueGetterParams) => {
        const raw = params.row[fieldId];
        const value = Number(raw);
        return Number.isNaN(value) ? null : value;
      };
    }

    // Apply lookup transformation for ID columns (e.g., worship_id -> "1 - Saturday - Bayview")
    if (field.lookupKey) {
      column.valueGetter = (params: GridValueGetterParams) => {
        const raw = params.row[fieldId];
        return getLookupLabel(field.lookupKey!, raw);
      };
    }

    return column;
  });

  // Add view button
  columns.splice(0, 0, {
    field: "actions",
    headerName: "",
    width: 100,
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      return (
        <Button
          color={params.row["is_local"] ? "success" : "primary"}
          onClick={() => navigate(`/${pageId}/form/${params.row[primaryKey]}`)}
          variant="contained"
        >
          {t("form.edit")}
        </Button>
      );
    },
  });

  if (!isCurrentPageAuthorized) {
    return null;
  }

  if (!initialState) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          mt={2}
          gutterBottom
          sx={{ color: "#46505A" }}
        >
          {title}
        </Typography>
        <IconButton
          color="primary"
          aria-label="New"
          size="large"
          onClick={() => navigate(`/${pageId}/form`)}
        >
          <AddIcon fontSize="inherit" />
        </IconButton>
      </Box>

      <Box sx={{ height: 500 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          density="comfortable"
          getRowHeight={() => "auto"}
          getEstimatedRowHeight={() => 100}
          initialState={{
            ...DEFAULT_INITIAL_STATE,
            ...initialState,
          }}
          apiRef={apiRef}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          getRowId={(row) => row[primaryKey]}
        />
      </Box>
    </>
  );
};

export default DataList;
